import { useEffect, useRef } from 'react';

type VisualizerProps = {
  analyser: AnalyserNode | null;
  isPlaying: boolean;
  accent?: string;
};

const POINT_COUNT = 64;
const FRAME_INTERVAL = 1000 / 60;
const TAU = Math.PI * 2;

const CYAN = { r: 85, g: 216, b: 207 };
const LIME = { r: 201, g: 243, b: 108 };

const mix = (a: typeof CYAN, b: typeof CYAN, t: number) => ({
  r: Math.round(a.r + (b.r - a.r) * t),
  g: Math.round(a.g + (b.g - a.g) * t),
  b: Math.round(a.b + (b.b - a.b) * t),
});

const rgba = (c: { r: number; g: number; b: number }, alpha: number) =>
  `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;

const averageBand = (data: Uint8Array<ArrayBuffer>, from: number, to: number) => {
  const start = Math.min(data.length - 1, Math.max(0, from));
  const end = Math.min(data.length, Math.max(start + 1, to));
  let total = 0;
  for (let index = start; index < end; index += 1) total += data[index];
  return total / Math.max(1, end - start) / 255;
};

// One soft, closed, gently-morphing halo — no spectrum spikes, no ticks.
const traceHalo = (
  context: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  radii: Float32Array<ArrayBuffer>,
  rotation: number,
) => {
  const count = radii.length;
  const point = (index: number) => {
    const angle = rotation - Math.PI / 2 + (index / count) * TAU;
    const radius = radii[((index % count) + count) % count];
    return { x: centerX + Math.cos(angle) * radius, y: centerY + Math.sin(angle) * radius };
  };
  context.beginPath();
  const first = point(0);
  const last = point(count - 1);
  context.moveTo((last.x + first.x) / 2, (last.y + first.y) / 2);
  for (let index = 0; index < count; index += 1) {
    const current = point(index);
    const next = point(index + 1);
    context.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2);
  }
  context.closePath();
};

export default function Visualizer({ analyser, isPlaying, accent = '#c9f36c' }: VisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playingRef = useRef(isPlaying);

  useEffect(() => {
    playingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const frequencyData = analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
    const radii = new Float32Array(POINT_COUNT);

    let animationFrame = 0;
    let lastFrame = 0;
    let phase = 0;
    let lowEnergy = 0;
    let midEnergy = 0;

    const draw = (timestamp = 0) => {
      animationFrame = window.requestAnimationFrame(draw);
      const frameInterval = playingRef.current ? FRAME_INTERVAL : FRAME_INTERVAL * 2;
      if (document.hidden || (!reduceMotion && timestamp - lastFrame < frameInterval)) return;
      if (reduceMotion && lastFrame > 0) return;
      const delta = lastFrame ? Math.min(2.2, (timestamp - lastFrame) / FRAME_INTERVAL) : 1;
      lastFrame = timestamp || 1;

      const rect = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const renderWidth = Math.max(1, Math.floor(width * pixelRatio));
      const renderHeight = Math.max(1, Math.floor(height * pixelRatio));
      if (canvas.width !== renderWidth || canvas.height !== renderHeight) {
        canvas.width = renderWidth;
        canvas.height = renderHeight;
      }
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      context.clearRect(0, 0, width, height);

      const active = playingRef.current && analyser && frequencyData;
      if (active) analyser.getByteFrequencyData(frequencyData);

      const rawLow = active ? averageBand(frequencyData, 2, 12) : 0.04;
      const rawMid = active ? averageBand(frequencyData, 12, 48) : 0.03;
      // Heavy smoothing keeps the motion meditative — a slow breathing swell.
      lowEnergy = lowEnergy + (rawLow - lowEnergy) * 0.08 * delta;
      midEnergy = midEnergy + (rawMid - midEnergy) * 0.06 * delta;
      phase += (active ? 0.004 + midEnergy * 0.004 : 0.0022) * delta;

      const centerX = width / 2;
      const centerY = height / 2;
      const scale = Math.min(width, height);
      const baseRadius = Math.min(width * 0.44, height * 0.44, scale * 0.46, 208);
      const breathing = Math.sin(phase * 1.4) * (3 + lowEnergy * 6);
      const energy = lowEnergy * 0.6 + midEnergy * 0.4;

      // Gently morphing shape from a few low-frequency lobes — organic, never spiky.
      for (let index = 0; index < POINT_COUNT; index += 1) {
        const position = index / POINT_COUNT;
        const lobeA = Math.sin(position * TAU * 2 + phase * 0.8) * (2 + midEnergy * 7);
        const lobeB = Math.sin(position * TAU * 3 - phase * 0.55) * (1.4 + midEnergy * 4);
        const lobeC = Math.sin(position * TAU * 5 + phase * 0.35) * (0.8 + lowEnergy * 2);
        radii[index] = baseRadius + breathing + lowEnergy * 5 + lobeA + lobeB + lobeC;
      }

      // ---- soft breathing nebula behind the timer ----
      const coreRadius = baseRadius * (1.05 + lowEnergy * 0.35);
      const nebula = context.createRadialGradient(centerX, centerY, baseRadius * 0.1, centerX, centerY, coreRadius);
      const glow = 0.08 + energy * 0.18;
      nebula.addColorStop(0, rgba(mix(CYAN, LIME, 0.35), glow));
      nebula.addColorStop(0.55, rgba(CYAN, glow * 0.4));
      nebula.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.save();
      context.globalCompositeOperation = 'lighter';
      context.fillStyle = nebula;
      context.beginPath();
      context.arc(centerX, centerY, coreRadius, 0, TAU);
      context.fill();
      context.restore();

      // ---- one soft halo ring (blurred wide glow + faint crisp line) ----
      const haloGradient = context.createLinearGradient(
        centerX - baseRadius,
        centerY - baseRadius,
        centerX + baseRadius,
        centerY + baseRadius,
      );
      haloGradient.addColorStop(0, rgba(CYAN, 0.5));
      haloGradient.addColorStop(0.5, rgba(mix(CYAN, LIME, 0.6), 0.5));
      haloGradient.addColorStop(1, rgba(LIME, 0.42));

      context.save();
      context.globalCompositeOperation = 'lighter';

      // wide diffuse halo
      traceHalo(context, centerX, centerY, radii, phase * 0.08);
      context.strokeStyle = rgba(CYAN, 0.03 + energy * 0.03);
      context.lineWidth = 22 + lowEnergy * 18;
      context.shadowColor = rgba(CYAN, 0.35);
      context.shadowBlur = 30 + lowEnergy * 24;
      context.stroke();

      // faint defining line
      traceHalo(context, centerX, centerY, radii, phase * 0.08);
      context.strokeStyle = haloGradient;
      context.globalAlpha = 0.35 + energy * 0.25;
      context.lineWidth = 1.1 + midEnergy * 0.8;
      context.shadowColor = accent;
      context.shadowBlur = 8 + midEnergy * 10;
      context.stroke();
      context.globalAlpha = 1;
      context.restore();
    };

    draw();
    return () => window.cancelAnimationFrame(animationFrame);
  }, [accent, analyser]);

  return <canvas ref={canvasRef} className="visualizer-canvas" aria-hidden="true" />;
}
